import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import FeaturedPropertyCard from "@/components/FeaturedPropertyCard";
import SearchEntry from "@/components/SearchEntry";
import ValuationEntry from "@/components/ValuationEntry";
import SEO from "@/components/SEO";
import LogoLoader, { hasSeenHomeLoader } from "@/components/LogoLoader";
import HeroSearchPanel from "@/components/HeroSearchPanel";
import { API } from "@/config/api";
import { submitSearchInquiry } from "@/utils/submitSearchInquiry";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

const HERO_VIDEO_URL =
  process.env.REACT_APP_HERO_VIDEO_URL || "/byron-header-video-2.mp4";
const HERO_FALLBACK_IMAGE =
  process.env.REACT_APP_HERO_FALLBACK_URL || "/Luxury_villa_evening_Byron.png";

const HOME_SERVICES = [
  {
    id: "acquisition",
    image: "/Luxury_villa_evening_Byron.png",
    href: "/acheter",
    titleKey: "home.serviceAcquisition",
    descKey: "home.serviceAcquisitionDesc",
    ctaKey: "home.serviceAcquisitionCta",
  },
  {
    id: "sale",
    image: "/Villa with pool_Byron.png",
    href: "/contact",
    titleKey: "home.serviceSale",
    descKey: "home.serviceSaleDesc",
    ctaKey: "home.serviceSaleCta",
  },
  {
    id: "off-market",
    image: "/Chambesy_Byron.png",
    href: "/off-market",
    titleKey: "home.serviceOffMarket",
    descKey: "home.serviceOffMarketDesc",
    ctaKey: "home.serviceOffMarketCta",
  },
  {
    id: "advisory",
    image: "/Lake view Appt_Byron.png",
    href: "/contact",
    titleKey: "home.serviceAdvisory",
    descKey: "home.serviceAdvisoryDesc",
    ctaKey: "home.serviceAdvisoryCta",
  },
];

const revealProps = (reduced, delay = 0) =>
  reduced
    ? {
        initial: { opacity: 1, y: 0 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-8%" },
        transition: { duration: 0 },
      }
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-8%" },
        transition: { duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] },
      };

const imageRevealProps = (reduced, delay = 0) =>
  reduced
    ? {
        initial: { opacity: 1, scale: 1 },
        whileInView: { opacity: 1, scale: 1 },
        viewport: { once: true, margin: "-10%" },
        transition: { duration: 0 },
      }
    : {
        initial: { opacity: 0, scale: 1.04 },
        whileInView: { opacity: 1, scale: 1 },
        viewport: { once: true, margin: "-10%" },
        transition: { duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] },
      };

const Home = () => {
  const { t, language } = useLanguage();
  const prefersReducedMotion = useReducedMotion();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroReady, setHeroReady] = useState(false);
  const [heroFailed, setHeroFailed] = useState(false);
  const [showLoader, setShowLoader] = useState(() => !hasSeenHomeLoader());
  const heroRef = useRef(null);
  const videoRef = useRef(null);

  const [searchMode, setSearchMode] = useState("buy");
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [formStep, setFormStep] = useState("criteria");
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    location: "",
    maxPrice: "",
    minSurface: "",
    bedrooms: "",
    propertyType: "",
  });
  const [contact, setContact] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? [0, 0] : [0, 56]
  );
  const heroScale = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? [1, 1] : [1, 1.06]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propertiesRes, locationsRes, typesRes] = await Promise.all([
          axios.get(`${API}/properties/featured`),
          axios.get(`${API}/locations`),
          axios.get(`${API}/property-types`),
        ]);
        setFeaturedProperties(propertiesRes.data.slice(0, 3));
        setLocations(locationsRes.data);
        setPropertyTypes(typesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // If video is already ready (cached), mark hero ready immediately
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.readyState >= 2) {
      setHeroReady(true);
    }
  }, []);

  const markHeroReady = () => setHeroReady(true);

  const handleHeroError = () => {
    setHeroFailed(true);
    setHeroReady(true);
  };

  const handleCriteriaContinue = (e) => {
    e.preventDefault();
    setFormStep("contact");
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (
      !contact.first_name.trim() ||
      !contact.last_name.trim() ||
      !contact.email.trim() ||
      !contact.phone.trim()
    ) {
      toast.error(
        language === "fr"
          ? "Veuillez remplir tous les champs obligatoires"
          : "Please fill in all required fields"
      );
      return;
    }

    setSubmitting(true);
    try {
      await submitSearchInquiry({
        searchType: searchMode === "buy" ? "acheter" : "louer",
        sourcePage: "/",
        searchMode: searchMode === "buy" ? "buy" : "rent",
        filters,
        contact,
      });
      setFormStep("success");
      toast.success(
        language === "fr"
          ? "Votre demande a bien été envoyée"
          : "Your enquiry has been sent"
      );
    } catch (error) {
      const detail = error.response?.data?.detail;
      const fallback =
        language === "fr" ? "Échec de l'envoi" : "Failed to send enquiry";
      toast.error(typeof detail === "string" ? detail : fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const buyPriceRanges = [
    { value: "", label: t("home.purchaseBudget") || "Budget d'achat" },
    { value: "1000000", label: "CHF 1M" },
    { value: "2000000", label: "CHF 2M" },
    { value: "3000000", label: "CHF 3M" },
    { value: "5000000", label: "CHF 5M" },
    { value: "10000000", label: "CHF 10M" },
    { value: "20000000", label: "CHF 20M" },
    { value: "50000000", label: "CHF 50M+" },
  ];

  const rentPriceRanges = [
    { value: "", label: t("home.monthlyBudget") || "Budget mensuel" },
    { value: "3000", label: "CHF 3'000 / mois" },
    { value: "5000", label: "CHF 5'000 / mois" },
    { value: "6000", label: "CHF 6'000 / mois" },
    { value: "8000", label: "CHF 8'000 / mois" },
    { value: "10000", label: "CHF 10'000 / mois" },
    { value: "15000", label: "CHF 15'000 / mois" },
    { value: "20000", label: "CHF 20'000+ / mois" },
  ];

  const priceRanges = searchMode === "rent" ? rentPriceRanges : buyPriceRanges;

  const surfaceOptions = [
    { value: "", label: t("home.minSurface") },
    { value: "100", label: "100 m²" },
    { value: "200", label: "200 m²" },
    { value: "300", label: "300 m²" },
    { value: "500", label: "500 m²" },
    { value: "1000", label: "1000 m²+" },
  ];

  const bedroomOptions = [
    { value: "", label: t("home.anyBedrooms") },
    { value: "2", label: "2+" },
    { value: "3", label: "3+" },
    { value: "4", label: "4+" },
    { value: "5", label: "5+" },
    { value: "7", label: "7+" },
  ];

  const locationOptions = [
    { value: "", label: t("home.locationPlaceholder") },
    ...locations.map((loc) => ({ value: loc, label: loc })),
  ];

  const propertyTypeOptions = [
    { value: "", label: t("home.allTypes") },
    ...propertyTypes.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
    })),
  ];

  return (
    <main data-testid="home-page">
      <SEO
        title={language === "fr" ? "Accueil" : "Home"}
        description={
          language === "fr"
            ? "Byrōn Properties - Votre partenaire de confiance pour l'immobilier de prestige à Genève. Découvrez notre sélection exclusive de villas, appartements et penthouses de luxe."
            : "Byrōn Properties — your partner for prestige real estate in Geneva. Discover an exclusive selection of villas, apartments and penthouses."
        }
        canonical="/"
      />

      {showLoader && (
        <LogoLoader
          heroReady={heroReady}
          onComplete={() => setShowLoader(false)}
        />
      )}

      {/* Hero — framed media with restrained scroll-linked movement */}
      <section className="relative bg-byron-bg" data-testid="hero-section">
        <div className="header-spacer bg-byron-bg" />

        <div className="media-frame-inset">
          <div
            ref={heroRef}
            className="relative"
            style={{
              height:
                "calc(100svh - var(--header-height) - var(--page-gutter) - 4.5rem)",
              minHeight: "420px",
            }}
          >
            {/* Media clipped separately so the ivory panel can expand “More filters” */}
            <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
              <motion.div
                className="absolute inset-0 will-change-transform"
                style={{ y: heroY, scale: heroScale }}
              >
                {!heroFailed ? (
                  <video
                    ref={videoRef}
                    src={HERO_VIDEO_URL}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={HERO_FALLBACK_IMAGE}
                    onCanPlay={markHeroReady}
                    onLoadedData={markHeroReady}
                    onError={handleHeroError}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: "center center" }}
                    data-testid="hero-video"
                  />
                ) : (
                  <img
                    src={HERO_FALLBACK_IMAGE}
                    alt=""
                    onLoad={markHeroReady}
                    onError={markHeroReady}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="hero-fallback-image"
                  />
                )}
              </motion.div>

              {/* Soft gradient for ivory panel readability — not a heavy dark strip */}
              <div
                className="absolute inset-0 pointer-events-none z-10"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(26,26,26,0.18) 0%, rgba(26,26,26,0.28) 55%, rgba(26,26,26,0.38) 100%)",
                }}
                aria-hidden="true"
              />
            </div>

            <div className="absolute inset-0 flex items-center justify-center z-20 overflow-visible px-0">
              <HeroSearchPanel
                language={language}
                t={t}
                searchMode={searchMode}
                setSearchMode={setSearchMode}
                filters={filters}
                setFilters={setFilters}
                contact={contact}
                setContact={setContact}
                formStep={formStep}
                setFormStep={setFormStep}
                showMoreOptions={showMoreOptions}
                setShowMoreOptions={setShowMoreOptions}
                submitting={submitting}
                locationOptions={locationOptions}
                priceRanges={priceRanges}
                surfaceOptions={surfaceOptions}
                bedroomOptions={bedroomOptions}
                propertyTypeOptions={propertyTypeOptions}
                onCriteriaContinue={handleCriteriaContinue}
                onEnquirySubmit={handleEnquirySubmit}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Hero Text */}
      <section className="bg-byron-bg pt-4 md:pt-5 pb-8 md:pb-10">
        <div className="container-byron">
          <motion.div
            {...revealProps(prefersReducedMotion)}
            className="max-w-xl"
          >
            <p className="label-text mb-3">{t("home.heroSubtitle")}</p>
            <h1 className="heading-primary mb-4">
              {t("home.heroTitle")}{" "}
              <em className="italic">{t("home.heroTitleAccent")}</em>
            </h1>
            <p className="body-text max-w-md">{t("home.heroDescription")}</p>
          </motion.div>
        </div>

        <div className="container-byron mt-6 md:mt-8">
          <div className="w-full h-px bg-[#E0DCD6]" />
        </div>
      </section>

      {/* Featured Properties */}
      <section
        className="py-10 md:py-14 bg-byron-bg"
        data-testid="featured-properties"
      >
        <div className="container-byron">
          <motion.div
            {...revealProps(prefersReducedMotion)}
            className="mb-6 md:mb-8"
          >
            <p className="label-text mb-3">{t("home.featuredLabel")}</p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="heading-primary">{t("home.selectedProperties")}</h2>
              <Link
                to="/acheter"
                className="label-text text-[#1A1A1A] hover:text-[#8B7355] transition-colors inline-flex items-center gap-2 group"
                data-testid="view-all-properties"
              >
                {t("home.viewAll")}
                <ArrowRight
                  size={12}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="skeleton aspect-[16/10]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12">
              {featuredProperties.slice(0, 2).map((property, index) => (
                <motion.div
                  key={property.id}
                  {...imageRevealProps(prefersReducedMotion, index * 0.08)}
                >
                  <FeaturedPropertyCard
                    property={property}
                    index={index}
                    variant="landscape"
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Off-Market Teaser */}
      <section
        className="py-10 md:py-14 bg-byron-bg"
        data-testid="off-market-teaser"
      >
        <div className="container-byron">
          <motion.div {...imageRevealProps(prefersReducedMotion)}>
            <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden media-frame mb-6">
              <video
                src="/Infinity_Pool_Byron.mp4"
                autoPlay={!prefersReducedMotion}
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            {...revealProps(prefersReducedMotion, 0.12)}
            className="max-w-xl"
          >
            <p className="label-text mb-3">{t("properties.heroSubtitle")}</p>
            <h2 className="heading-primary mb-5">{t("home.offMarketTitle")}</h2>
            <p className="body-text mb-6">{t("home.offMarketDescription")}</p>
            <Link
              to="/off-market"
              className="label-text text-[#1A1A1A] hover:text-[#8B7355] transition-colors inline-flex items-center gap-2 group"
              data-testid="off-market-cta"
            >
              {t("home.requestAccess")}
              <ArrowRight
                size={12}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </motion.div>
        </div>
      </section>

      <SearchEntry />

      {/* Services */}
      <section
        className="py-10 md:py-14 bg-byron-bg overflow-hidden"
        data-testid="services-section"
      >
        <div className="container-byron">
          <motion.div
            {...revealProps(prefersReducedMotion)}
            className="mb-6 md:mb-8 max-w-2xl"
          >
            <h2 className="heading-primary mb-5">{t("home.servicesTitle")}</h2>
            <p className="body-text">{t("home.servicesIntro")}</p>
          </motion.div>
        </div>

        <div className="container-byron !pr-0">
          <Carousel
            opts={{ align: "start", dragFree: true }}
            className="w-full cursor-grab active:cursor-grabbing"
          >
            <CarouselContent className="-ml-4 md:-ml-5">
              {HOME_SERVICES.map((service, index) => (
                <CarouselItem
                  key={service.id}
                  className="pl-4 md:pl-5 basis-[78%] sm:basis-[55%] md:basis-[42%] lg:basis-[32%]"
                >
                  <motion.div {...imageRevealProps(prefersReducedMotion, index * 0.05)}>
                    <Link
                      to={service.href}
                      className="group block"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="relative aspect-[3/4] overflow-hidden media-frame mb-5">
                        <img
                          src={service.image}
                          alt={t(service.titleKey)}
                          className={`absolute inset-0 w-full h-full object-cover ${
                            prefersReducedMotion
                              ? ""
                              : "transition-transform duration-700 group-hover:scale-[1.03]"
                          }`}
                        />
                      </div>
                      <h3 className="heading-secondary mb-2">
                        {t(service.titleKey)}
                      </h3>
                      <p className="body-text mb-4 max-w-sm">
                        {t(service.descKey)}
                      </p>
                      <span className="label-text text-[#1A1A1A] group-hover:text-[#8B7355] transition-colors inline-flex items-center gap-2">
                        {t(service.ctaKey)}
                        <ArrowRight size={12} />
                      </span>
                    </Link>
                  </motion.div>
                </CarouselItem>
              ))}
              <CarouselItem
                className="pl-4 md:pl-5 basis-4 md:basis-8 grow-0 shrink-0"
                aria-hidden
              />
            </CarouselContent>
          </Carousel>
        </div>
      </section>

      {/* Property Types */}
      <section
        className="py-10 md:py-14 bg-byron-bg"
        data-testid="property-types-section"
      >
        <div className="container-byron">
          <motion.div
            {...revealProps(prefersReducedMotion)}
            className="mb-6 md:mb-8"
          >
            <h2 className="heading-primary">Nos univers</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                href: "/acheter?property_type=chalet",
                src: process.env.REACT_APP_CHALET_IMAGE_URL || "/Chalet.png",
                title: "Chalets en montagne",
              },
              {
                href: "/acheter?property_type=villa",
                src:
                  process.env.REACT_APP_VILLA_IMAGE_URL ||
                  "/living-room-view.png",
                title: "Villas contemporaines",
              },
              {
                href: "/acheter?property_type=townhouse",
                src:
                  process.env.REACT_APP_MAITRE_IMAGE_URL || "/maison-maitre.png",
                title: "Maisons de maître",
              },
            ].map((item, index) => (
              <motion.div
                key={item.href}
                {...revealProps(prefersReducedMotion, index * 0.08)}
                className="group"
              >
                <Link to={item.href} className="block">
                  <div className="relative aspect-[4/5] overflow-hidden media-frame mb-4">
                    <img
                      src={item.src}
                      alt={item.title}
                      className={`absolute inset-0 w-full h-full object-cover ${
                        prefersReducedMotion
                          ? ""
                          : "transition-transform duration-700 group-hover:scale-105"
                      }`}
                    />
                  </div>
                  <h3 className="heading-secondary mb-2">{item.title}</h3>
                  <span className="label-text text-[#1A1A1A] group-hover:text-[#8B7355] transition-colors inline-flex items-center gap-2">
                    Voir plus
                    <ArrowRight
                      size={12}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ValuationEntry />
    </main>
  );
};

export default Home;
